/** Bounds applied to untrusted input (stored documents, clipboard, imports) before deserialization. */
export interface DocumentLimits {
  maxBytes: number;
  maxNodes: number;
  maxDepth: number;
  /**
   * The most rows and columns one table may have, counting what merged cells cover. Optional so
   * that limits written before tables existed stay valid; the defaults apply when left out.
   */
  maxTableRows?: number;
  maxTableColumns?: number;
  /**
   * The largest file the editor takes as an attachment, in bytes. It bounds what comes in, not
   * what is stored: a document never holds the bytes, and lowering the limit leaves every document
   * readable. Optional, like the table bounds.
   */
  maxAttachmentBytes?: number;
}

export const DEFAULT_LIMITS: Required<DocumentLimits> = {
  maxBytes: 8 * 1024 * 1024,
  maxNodes: 200_000,
  maxDepth: 64,
  maxTableRows: 500,
  maxTableColumns: 50,
  maxAttachmentBytes: 25 * 1024 * 1024,
};

/** The table bounds in force: the caller's, or the defaults where it names none. */
export const tableLimits = (limits: DocumentLimits): { rows: number; columns: number } => ({
  rows: limits.maxTableRows ?? DEFAULT_LIMITS.maxTableRows,
  columns: limits.maxTableColumns ?? DEFAULT_LIMITS.maxTableColumns,
});

/** The attachment bound in force: the caller's, or the default where it names none. */
export const attachmentLimits = (limits: DocumentLimits): { bytes: number } => ({
  bytes: limits.maxAttachmentBytes ?? DEFAULT_LIMITS.maxAttachmentBytes,
});
